// src/components/Dashboard/Inventory/Maintenance_vs_Transportation_Cost.js
import React from 'react';
import { Radar } from 'react-chartjs-2';
import { typography } from '../../../styles/typography.js';
import Card from '../../ui/card.js';

const MaintenanceVsTransportationCost = ({
  isLoading,
  error,
  categoryData,
  summary,
  radarData,
  radarOptions,
  selectedDataset,
  onToggleDataset,
  onRetry,
}) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-[360px] gap-3 text-center">
          <div className="h-10 w-10 border-4 border-t-transparent border-blue-500 rounded-full animate-spin" />
          <p className={typography.p} style={{ color: '#333333' }}>
            Loading cost comparison data...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-[360px] gap-3 text-center">
          <p className={typography.p} style={{ color: '#DC2626' }}>
            {error}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      );
    }

    if (!categoryData.length) {
      return (
        <div className="flex flex-col items-center justify-center h-[360px] gap-3 text-center">
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p className={typography.p} style={{ color: '#666666' }}>
            No cost comparison data for this period
          </p>
          <p
            className={typography.p}
            style={{ color: '#999999', fontSize: '0.875rem' }}
          >
            Adjust the filter to see available transportation insights.
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex flex-row lg:flex-col gap-3 w-full lg:max-w-[160px]">
          <div className="p-4 bg-white/80 rounded-xl shadow-inner text-center w-full flex-1 flex flex-col items-center justify-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-black/60">
              Maintenance per Item
            </p>
            <p
              className={typography.h3}
              style={{ color: '#000000', marginTop: '0.35rem' }}
            >
              ₹
              {summary.averageMaintenance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
            <p className={`${typography.p} text-green-700 text-sm`}>
              {summary.maintenanceShare.toFixed(1)}% of combined avg
            </p>
          </div>
          <div className="p-4 bg-white/80 rounded-xl shadow-inner text-center w-full flex-1 flex flex-col items-center justify-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-black/60">
              Transportation per Maintenance
            </p>
            <p
              className={typography.h3}
              style={{ color: '#000000', marginTop: '0.35rem' }}
            >
              ₹
              {summary.averageTransportation.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
            <p className={`${typography.p} text-orange-600 text-sm`}>
              {summary.transportationShare.toFixed(1)}% of combined avg
            </p>
          </div>
        </div>

        <div className="w-full lg:w-[360px] flex-shrink-0 flex flex-col items-center">
          <div className="w-full flex flex-col items-center gap-4">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => onToggleDataset('maintenance')}
                className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                  selectedDataset === 'maintenance'
                    ? 'bg-[#9747FF] text-white border-[#9747FF]'
                    : 'bg-white/80 text-[#9747FF] border-[#9747FF]/40 hover:border-[#9747FF]'
                }`}
              >
                Maintenance
              </button>
              <button
                type="button"
                onClick={() => onToggleDataset('transportation')}
                className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                  selectedDataset === 'transportation'
                    ? 'bg-[#65CD69] text-white border-[#65CD69]'
                    : 'bg-white/80 text-[#065F2A] border-[#65CD69]/40 hover:border-[#65CD69]'
                }`}
              >
                Transportation
              </button>
            </div>
          </div>
          <div className="w-full max-w-[320px] h-[320px] relative">
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>

        <div className="flex-1 overflow-x-auto overflow-y-auto max-h-[360px] rounded-lg border border-gray-300">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr>
                <th
                  className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-100 border-b border-neutral-200"
                  style={{ fontFamily: 'inherit' }}
                >
                  type
                </th>
                <th
                  className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-200 border-b border-neutral-200"
                  style={{ fontFamily: 'inherit' }}
                >
                  Maintenance per Item
                </th>
                <th
                  className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-100 border-b border-neutral-200"
                  style={{ fontFamily: 'inherit' }}
                >
                  Transportation per Maintenance
                </th>
                <th
                  className="px-4 py-3 text-left text-sm font-semibold text-black bg-neutral-200 border-b border-neutral-200"
                  style={{ fontFamily: 'inherit' }}
                >
                  Combined Average
                </th>
              </tr>
            </thead>
            <tbody>
              {categoryData.map((row, index) => (
                <tr
                  key={`${row.type}-comparison`}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}
                >
                  <td className="px-4 py-3 text-sm text-black border-b border-neutral-100">
                    {row.type}
                  </td>
                  <td className="px-4 py-3 text-sm text-black border-b border-neutral-100">
                    ₹
                    {row.averageMaintenance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-4 py-3 text-sm text-black border-b border-neutral-100">
                    ₹
                    {row.averageTransportation.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-4 py-3 text-sm text-black font-semibold border-b border-neutral-100">
                    ₹
                    {row.combinedAverage.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-10">
      <h2
        className={typography.h3}
        style={{ color: '#000000', marginBottom: '1rem' }}
      >
        Maintenance vs Transportation Cost
      </h2>
      <Card.Gradient className="p-6">{renderContent()}</Card.Gradient>
    </div>
  );
};

export default MaintenanceVsTransportationCost;

